// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { createTypedReducerWithImmer } from "@insite/client-framework/Common/CreateTypedReducer";
import { API_URL_CURRENT_FRAGMENT } from "@insite/client-framework/Services/ApiService";
import { GetShipTosApiParameter } from "@insite/client-framework/Services/CustomersService";
import { SettingsModel } from "@insite/client-framework/Services/SettingsService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { assignById, setDataViewLoaded, setDataViewLoading } from "@insite/client-framework/Store/Data/DataState";
import { ShipTosState } from "@insite/client-framework/Store/Data/ShipTos/ShipTosState";
import AddressesState from "@insite/client-framework/Store/Pages/Addresses/AddressesState";
import { AnyAction, reducers } from "@insite/client-framework/Store/Reducers";
import { ShipToCollectionModel, ShipToModel } from "@insite/client-framework/Types/ApiModels";
import { Draft } from "immer";

type CompanySavedAddressesState = AddressesState & Readonly<ShipTosState>;

const initialState: CompanySavedAddressesState = {
    getShipTosParameter: {
        filter: "",
        page: 1,
        expand: ["validation"],
        exclude: ["showAll", "oneTime", "billTo"],
    },
    isLoading: {},
    byId: {},
    dataViews: {},
};

const reducer = {
    "xCompanySavedAddresses/UpdateSearchFields": (
        draft: Draft<AddressesState>,
        action: { parameter: GetShipTosApiParameter },
    ) => {
        draft.getShipTosParameter = { ...draft.getShipTosParameter, ...action.parameter };
    },

    "xCompanySavedAddresses/BeginLoadShipTos": (
        draft: Draft<ShipTosState>,
        action: { parameter: GetShipTosApiParameter },
    ) => {
        setDataViewLoading(draft, action.parameter);
    },

    "xCompanySavedAddresses/CompleteLoadShipTos": (
        draft: Draft<ShipTosState>,
        action: { parameter: GetShipTosApiParameter; collection: ShipToCollectionModel },
    ) => {
        setDataViewLoaded(draft, action.parameter, action.collection, collection => collection.shipTos!);
    },

    "xCompanySavedAddresses/BeginLoadShipTo": (draft: Draft<ShipTosState>, action: { id: string }) => {
        draft.isLoading[action.id] = true;
    },

    "xCompanySavedAddresses/CompleteLoadShipTo": (
        draft: Draft<ShipTosState>,
        action: { model: ShipToModel; isCurrent?: boolean },
    ) => {
        if (action.isCurrent) {
            delete draft.isLoading[API_URL_CURRENT_FRAGMENT];
            draft.currentId = action.model.id;
        } else {
            delete draft.isLoading[action.model.id];
        }
        assignById(draft, action.model);
    },

    "xCompanySavedAddresses/ResetDataViews": (draft: Draft<ShipTosState>) => {
        draft.dataViews = {};
    },

    "xCompanySavedAddresses/Reset": () => {
        return initialState;
    },

    "xCompanySavedAddresses/BeginDeleteCompanySavedAddress": (draft: Draft<ShipTosState>) => {},
    "xCompanySavedAddresses/CompleteDeleteCompanySavedAddress": (draft: Draft<ShipTosState>) => {},
    "Context/CompleteLoadSettings": (draft: Draft<AddressesState>, action: { settings: SettingsModel }) => {
        draft.getShipTosParameter.pageSize = action.settings.settingsCollection.websiteSettings.defaultPageSize;
    },
    "xCompanySavedAddresses/SetPageSize": (draft: Draft<AddressesState>, action: { pageSize: number }) => {
        draft.getShipTosParameter.pageSize = action.pageSize;
  
    },
};

const xCompanySavedAddresses = createTypedReducerWithImmer(initialState, reducer);

(reducers as any).xCompanySavedAddresses = xCompanySavedAddresses;

export type xCompanySavedAddressesState = {
    xCompanySavedAddresses: CompanySavedAddressesState;
} & ApplicationState;

type xCompanySavedAddressesReducers = {
    companySavedAddresses: typeof xCompanySavedAddresses;
};

export type xCompanySavedAddressesActions =
    | Parameters<xCompanySavedAddressesReducers[keyof xCompanySavedAddressesReducers]>[1]
    | AnyAction;
